import React, { forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export type IconButtonVariant = 'default' | 'primary' | 'secondary' | 'danger' | 'success' | 'warning';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: IconDefinition;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  tooltip?: string;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(({
  icon,
  variant = 'default',
  size = 'md',
  tooltip,
  className = '',
  ...props
}, ref) => {
  const baseClasses = 'inline-flex items-center justify-center rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const sizeClasses = {
    sm: 'p-1',
    md: 'p-2',
    lg: 'p-3'
  };
  
  const iconSizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };
  
  const variantClasses = {
    default: 'text-gray-400 hover:text-gray-600 focus:ring-gray-500',
    primary: 'text-blue-600 hover:text-blue-700 hover:bg-blue-50 focus:ring-blue-500',
    secondary: 'text-gray-600 hover:text-gray-700 hover:bg-gray-50 focus:ring-gray-500',
    danger: 'text-red-600 hover:text-red-700 hover:bg-red-50 focus:ring-red-500',
    success: 'text-green-600 hover:text-green-700 hover:bg-green-50 focus:ring-green-500',
    warning: 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 focus:ring-yellow-500'
  };
  
  const buttonClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${className}`.trim();

  return (
    <button
      ref={ref}
      className={buttonClasses}
      title={tooltip}
      {...props}
    >
      <FontAwesomeIcon 
        icon={icon} 
        className={iconSizeClasses[size]}
      />
    </button>
  );
});

IconButton.displayName = 'IconButton';

export default IconButton;